import isEqual from 'lodash/isEqual'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { Group, Image, Layer, Rect, Stage, Text } from 'react-konva'
import { CustomButton, InputWrapper } from '../../../../../components'
import { WemoloDropdown, WemoloTypography } from '../../../../../components/Wemolo'
import { useFormSaveStore } from '../../../../../utils'
import { StationContext } from '../../../StationContext'
import { TrajectoryConfigurationContext } from '../../TrajectoryConfigurationContext'
import { StationTrajectoryPanel } from '../StationTrajectoryPanel'
import TrajectoryViewLayer from '../TrajectoryViewLayer/TrajectoryViewLayer'
import Annotation from './components/Annotation'
import ValidationModal from './components/ValidationModal'
import ViewLayer from './components/ViewLayer'
import styles from './Canvas.module.css'

interface ILines {
    name: string
    ref: boolean
    points: number[]
}

function filterFiltersByViewModeIds(filters: any[], viewModeIds: string | any[]) {
    return filters
        .map((filter, index) => ({
            ...filter,
            viewModeId: index.toString()
        }))
        .filter((filter, index) => viewModeIds.includes(index.toString()))
}

const Canvas: React.FC<{
    polygon: any
}> = (props) => {
    const setFormChanges = useFormSaveStore((state) => state.setFormChanges)
    const [initialRender, setInitialRender] = useState(true)
    // Signature of the polygon last pushed in from `props.polygon` (i.e. loaded from the server).
    // The resync effect uses it to tell a server reload apart from a user edit, so a post-save
    // refetch (which can return not-yet-committed config) doesn't falsely mark the form dirty.
    const lastServerPolygonRef = useRef<string | null>(null)

    const stationContext = useContext(StationContext)
    const trajectoryConfigurationContext = useContext(TrajectoryConfigurationContext)
    const handleCloseValidationModal = () => trajectoryConfigurationContext.setValidationModalStatePolygon(false, '')
    const [position, setPosition] = useState([0, 0])
    const [viewConfig, setviewConfig] = useState<any>([])

    const [polygonDataContext, setPolygonDataContext] = useState({
        points: props.polygon ? props.polygon.points : [],
        flattenedPoints: props.polygon ? props.polygon.flattenedPoints : [],
        lines: props.polygon ? props.polygon.lines : []
    })
    const [points, setPoints] = useState<any>(props.polygon ? props.polygon.points : [])
    const [flattenedPoints, setFlattenedPoints] = useState(props.polygon ? props.polygon.flattenedPoints : [])
    const [lines, setLines] = useState<ILines[]>(props.polygon ? props.polygon.lines : [])
    const [isPolyComplete, setPolyComplete] = useState(false)
    const [isMouseOverStartPoint, setMouseOverStartPoint] = useState(false)
    const [draggingPointIndex, setDraggingPointIndex] = useState<number | null>(null)

    useEffect(() => {
        if (trajectoryConfigurationContext.viewMode && trajectoryConfigurationContext.filterData) {
            const viewModeIds = trajectoryConfigurationContext.viewMode.map((viewMode) => viewMode.id)
            const filteredFilters = filterFiltersByViewModeIds(trajectoryConfigurationContext.filterData, viewModeIds)
            setviewConfig(filteredFilters)
        }
    }, [trajectoryConfigurationContext.filterData, trajectoryConfigurationContext.viewMode])

    useEffect(() => {
        if (props.polygon === null) {
            lastServerPolygonRef.current = JSON.stringify({ points: [], lines: [] })
            setPoints([])
            setFlattenedPoints([])
            setLines([])
            setPolyComplete(false)
            return
        }
        // Keyed on points + lines only, matching the change check in the resync effect below.
        lastServerPolygonRef.current = JSON.stringify({
            points: props.polygon.points,
            lines: props.polygon.lines
        })
        setPoints(props.polygon.points)
        setFlattenedPoints(props.polygon.flattenedPoints)
        setLines(props.polygon.lines)
        setPolyComplete(true)
    }, [props.polygon, stationContext.loadingDeviceConfigSave])

    //MIRROR DATA IN CONTEXT STATE
    useEffect(() => {
        const polygonData = {
            points: points,
            flattenedPoints: flattenedPoints,
            lines: lines
        }
        const isContextDataOutOfSync =
            JSON.stringify(polygonDataContext.points) !== JSON.stringify(points) ||
            JSON.stringify(polygonDataContext.flattenedPoints) !== JSON.stringify(flattenedPoints) ||
            JSON.stringify(polygonDataContext.lines) !== JSON.stringify(lines)
        if (isContextDataOutOfSync) {
            trajectoryConfigurationContext.setData('polygonData', polygonData)
            setPolygonDataContext(polygonData)
            if (
                isEqual(polygonDataContext.lines, polygonData.lines) &&
                isEqual(polygonDataContext.points, polygonData.points)
            ) {
                //no changes
            } else {
                // Only a genuine user edit marks the form dirty. When the new data matches what was
                // just loaded from the server, this resync is a (possibly premature) refetch, not an
                // edit — leave the saved/clean state alone so Save doesn't need a second click.
                const polygonSignature = JSON.stringify({ points: polygonData.points, lines: polygonData.lines })
                const cameFromServerReload = lastServerPolygonRef.current === polygonSignature
                if (!initialRender && !cameFromServerReload) {
                    setFormChanges(true)
                }
                setInitialRender(false)
            }
        }
    }, [
        points,
        flattenedPoints,
        lines,
        polygonDataContext,
        trajectoryConfigurationContext,
        setFormChanges,
        initialRender
    ])

    useEffect(() => {
        if (!isPolyComplete) return

        setLines((prevLines) => {
            const lines_array = points.map((element: any, index: any, array: any) => {
                if (index === array.length - 1) {
                    try {
                        return {
                            name: `line_${index}`,
                            ref: prevLines[index].ref,
                            points: [element, array[0]]
                        }
                    } catch (error) {
                        return {
                            name: `line_${index}`,
                            ref: false,
                            points: [element, array[0]]
                        }
                    }
                }
                try {
                    return {
                        name: `line_${index}`,
                        ref: prevLines[index].ref,
                        points: [element, array[index + 1]]
                    }
                } catch (error) {
                    return {
                        name: `line_${index}`,
                        ref: false,
                        points: [element, array[index + 1]]
                    }
                }
            })
            return lines_array
        })
    }, [isPolyComplete, points])

    useEffect(() => {
        setFlattenedPoints(points.concat(isPolyComplete ? [] : position).reduce((a: any, b: any) => a.concat(b), []))
    }, [points, isPolyComplete, position])

    const getMousePos = (stage: any): [number, number] => {
        const pos = stage.getPointerPosition()
        return [Math.round(pos.x * 10) / 10, Math.round(pos.y * 10) / 10]
    }

    const findPointAtPos = (mousePos: [number, number]): number => {
        const hitRadius = 12
        for (let i = 0; i < points.length; i++) {
            const dx = points[i][0] - mousePos[0]
            const dy = points[i][1] - mousePos[1]
            if (Math.sqrt(dx * dx + dy * dy) <= hitRadius) return i
        }
        return -1
    }

    const distanceToSegment = (px: number, py: number, x1: number, y1: number, x2: number, y2: number): number => {
        const dx = x2 - x1
        const dy = y2 - y1
        const lenSq = dx * dx + dy * dy
        if (lenSq === 0) return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2)
        const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq))
        return Math.sqrt((px - (x1 + t * dx)) ** 2 + (py - (y1 + t * dy)) ** 2)
    }

    const findLineAtPos = (mousePos: [number, number]): number => {
        const hitDistance = 12
        for (let i = 0; i < lines.length; i++) {
            const p1 = lines[i].points[0]
            const p2 = lines[i].points[1]
            if (distanceToSegment(mousePos[0], mousePos[1], p1[0], p1[1], p2[0], p2[1]) <= hitDistance) return i
        }
        return -1
    }

    const handleMouseMove = (event: any) => {
        const stage = event.target.getStage()
        const mousePos = getMousePos(stage)
        setPosition(mousePos)

        if (draggingPointIndex !== null) {
            const clampedPos: [number, number] = [
                Math.max(0, Math.min(stage.width(), mousePos[0])),
                Math.max(0, Math.min(stage.height(), mousePos[1]))
            ]
            setPoints([...points.slice(0, draggingPointIndex), clampedPos, ...points.slice(draggingPointIndex + 1)])
            stage.container().style.cursor = 'move'
            return
        }

        if (isPolyComplete) {
            stage.container().style.cursor = findPointAtPos(mousePos) !== -1 ? 'move' : 'default'
            return
        }

        if (points.length >= 3) {
            const dx = points[0][0] - mousePos[0]
            const dy = points[0][1] - mousePos[1]
            setMouseOverStartPoint(Math.sqrt(dx * dx + dy * dy) <= 12)
        }
    }

    const handleMouseDown = (event: any) => {
        const stage = event.target.getStage()
        const mousePos = getMousePos(stage)

        if (isPolyComplete) {
            const pointIndex = findPointAtPos(mousePos)
            if (pointIndex !== -1) {
                setDraggingPointIndex(pointIndex)
                return
            }
            const lineIndex = findLineAtPos(mousePos)
            if (lineIndex !== -1) {
                setLines((prevLines) =>
                    prevLines.map((element, i) => (i === lineIndex ? { ...element, ref: !element.ref } : element))
                )
            }
            return
        }

        if (isMouseOverStartPoint && points.length >= 3) {
            setPolyComplete(true)
            setMouseOverStartPoint(false)
        } else {
            setPoints([...points, mousePos])
        }
    }

    useEffect(() => {
        const onGlobalMouseUp = () => setDraggingPointIndex(null)
        window.addEventListener('mouseup', onGlobalMouseUp)
        return () => window.removeEventListener('mouseup', onGlobalMouseUp)
    }, [])

    const handleMouseUp = () => {
        setDraggingPointIndex(null)
    }

    const undo = () => {
        setPoints(points.slice(0, -1))
        setPolyComplete(false)
        setPosition(points[points.length - 1])
        setLines([])
    }

    const reset = () => {
        setPoints([])
        setPolyComplete(false)
        setLines([])
    }

    return (
        <div className={styles.root}>
            <div className={styles.leftPanel}>
                <StationTrajectoryPanel>
                    {trajectoryConfigurationContext.data.enableEventClassificationData !== undefined ? (
                        <div className={styles.controls}>
                            <InputWrapper label="Enable Event Classification">
                                <WemoloDropdown
                                    size="medium"
                                    placeholder="Select Mode"
                                    options={[
                                        { label: 'ON', value: 'extrapolation' },
                                        { label: 'OFF', value: 'legacy_classification' },
                                        { label: 'OFF (Default)', value: 'default' }
                                    ]}
                                    value={
                                        trajectoryConfigurationContext.data.enableEventClassificationData.base ===
                                            'true' ||
                                        trajectoryConfigurationContext.data.enableEventClassificationData.base === true
                                            ? 'default'
                                            : trajectoryConfigurationContext.data.enableEventClassificationData
                                                    .value === 'extrapolation'
                                              ? 'extrapolation'
                                              : trajectoryConfigurationContext.data.enableEventClassificationData
                                                      .value === 'legacy_classification'
                                                ? 'legacy_classification'
                                                : undefined
                                    }
                                    onChange={(id) => {
                                        if (!id) return
                                        setFormChanges(true)
                                        if (id === 'default') {
                                            trajectoryConfigurationContext.setData('enableEventClassificationData', {
                                                base: true,
                                                value: 'legacy_classification'
                                            })
                                        } else if (id === 'extrapolation') {
                                            trajectoryConfigurationContext.setData('enableEventClassificationData', {
                                                base: false,
                                                value: 'extrapolation'
                                            })
                                        } else if (id === 'legacy_classification') {
                                            trajectoryConfigurationContext.setData('enableEventClassificationData', {
                                                base: false,
                                                value: 'legacy_classification'
                                            })
                                        }
                                    }}
                                />
                            </InputWrapper>
                            <div className={styles.columnFlush}>
                                <CustomButton
                                    onClick={undo}
                                    extraProps={{
                                        marginBottom: '16px'
                                    }}
                                >
                                    Undo
                                </CustomButton>
                                <CustomButton onClick={reset}>Reset</CustomButton>
                            </div>
                        </div>
                    ) : (
                        <WemoloTypography variant="body-medium" tag="div">
                            NO DATA AVAILABLE
                        </WemoloTypography>
                    )}
                </StationTrajectoryPanel>
            </div>
            <div className={styles.rightPanel}>
                <StationTrajectoryPanel>
                    <div className={styles.wrapper}>
                        <div className={styles.captionWrapper}>
                            <WemoloTypography variant="label-xsmall" tag="span">
                                {trajectoryConfigurationContext.imageSize.text}
                            </WemoloTypography>
                        </div>
                        <ValidationModal
                            show={trajectoryConfigurationContext.validationModalStatePolygon.show}
                            handleCloseValidationModal={handleCloseValidationModal}
                            error_text={trajectoryConfigurationContext.validationModalStatePolygon.description}
                        ></ValidationModal>
                        <div className={styles.column}>
                            <Stage
                                width={trajectoryConfigurationContext.imageSize.width}
                                height={trajectoryConfigurationContext.imageSize.height}
                                onMouseMove={handleMouseMove}
                                onMouseDown={handleMouseDown}
                                onMouseUp={handleMouseUp}
                            >
                                <Layer>
                                    {trajectoryConfigurationContext.image === undefined ? (
                                        <>
                                            <Group>
                                                <Rect
                                                    width={trajectoryConfigurationContext.imageSize.width}
                                                    height={trajectoryConfigurationContext.imageSize.height}
                                                    fill="black"
                                                />
                                                <Text
                                                    text={trajectoryConfigurationContext.imageSize.text}
                                                    x={10}
                                                    y={10}
                                                    fill="transparent"
                                                    fontSize={0}
                                                />
                                            </Group>
                                            <ViewLayer
                                                imageSize={trajectoryConfigurationContext.imageSize}
                                                viewConfig={viewConfig}
                                            ></ViewLayer>
                                            <TrajectoryViewLayer
                                                trajectory_data={
                                                    trajectoryConfigurationContext.trajectoryViewCoordinates
                                                }
                                                trajectories_vehicle={
                                                    trajectoryConfigurationContext.vehicleDetectionsTrajectoryView
                                                }
                                                trajectories_lp={
                                                    trajectoryConfigurationContext.lpDetectionsTrajectoryView
                                                }
                                                best_detection={trajectoryConfigurationContext.bestDetection}
                                                enable_view={trajectoryConfigurationContext.enableTrajectoryView}
                                            />
                                            <Annotation
                                                closed={isPolyComplete}
                                                points={points}
                                                flattenedPoints={flattenedPoints}
                                                lines={lines}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <Image
                                                image={trajectoryConfigurationContext.image}
                                                x={0}
                                                y={0}
                                                width={trajectoryConfigurationContext.imageSize.width}
                                                height={trajectoryConfigurationContext.imageSize.height}
                                            />
                                            <Group>
                                                <Rect width={0} height={0} fill="black" />
                                                <Text
                                                    text={trajectoryConfigurationContext.imageSize.text}
                                                    x={10}
                                                    y={10}
                                                    fill="transparent"
                                                    fontSize={0}
                                                />
                                            </Group>
                                            <ViewLayer
                                                imageSize={trajectoryConfigurationContext.imageSize}
                                                viewConfig={viewConfig}
                                            ></ViewLayer>
                                            <TrajectoryViewLayer
                                                trajectory_data={
                                                    trajectoryConfigurationContext.trajectoryViewCoordinates
                                                }
                                                trajectories_vehicle={
                                                    trajectoryConfigurationContext.vehicleDetectionsTrajectoryView
                                                }
                                                trajectories_lp={
                                                    trajectoryConfigurationContext.lpDetectionsTrajectoryView
                                                }
                                                best_detection={trajectoryConfigurationContext.bestDetection}
                                                enable_view={trajectoryConfigurationContext.enableTrajectoryView}
                                            />
                                            <Annotation
                                                closed={isPolyComplete}
                                                points={points}
                                                flattenedPoints={flattenedPoints}
                                                lines={lines}
                                            />
                                        </>
                                    )}
                                </Layer>
                            </Stage>
                        </div>
                    </div>
                </StationTrajectoryPanel>
            </div>
        </div>
    )
}

export default Canvas
